import _ from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Divider, Grid, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

import Typography from '@mui/material/Typography';

import BuildIcon from '@mui/icons-material/Build';
import SaveIcon from '@mui/icons-material/Save';

import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import moment from 'moment';
import ButtonWithProgress from '../../components/ButtonWithProgress.jsx';
import ConfirmationDialog from '../../components/ConfirmationDialog.jsx';
import EditForm from '../../components/EditForm/EditForm.jsx';
import EditFormAction from '../../components/EditForm/EditFormAction.jsx';
import EditFormContent from '../../components/EditForm/EditFormContent.jsx';
import EditFormField from '../../components/EditForm/EditFormField.jsx';
import EditFormFooter from '../../components/EditForm/EditFormFooter.jsx';
import EditFormHeader from '../../components/EditForm/EditFormHeader.jsx';
import EditFormMessage from '../../components/EditForm/EditFormMessage.jsx';
import FirebaseConsoleLink from '../../components/FirebaseConsoleLink.jsx';
import { appActions } from '../app/app-slice.js';
import { enterprisesActions } from './enterprises-slice.js';

// const useStyles = makeStyles((_theme) => ({
//   tooltip: {
//     maxWidth: 500,
//     // backgroundColor: theme.palette.background.default,
//   },
// }));

const useStyles = makeStyles((theme) => ({
  clsSecret: {
    backgroundColor: 'black',
    color: 'red',
  },
  clsSecretActive: {
    color: theme.palette.secondary.main,
  },
}));

function EditEnterprise({ onClose }) {
  const { clsSecretActive } = useStyles();
  const { isLoading } = useSelector((state) => state.app);
  const { selected, selectedContext } = useSelector(
    (state) => state.enterprises
  );
  const { metadata } = useSelector((state) => state.customers);
  const { data: users } = useSelector((state) => state.users);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
  } = useForm();

  const customersTypes = watch('customersTypes', selected.customersTypes ?? []);

  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] =
    React.useState(false);
  const dispatch = useDispatch();
  const { permissions } = useSelector((state) => state.auth);
  const { isSecretActive } = useSelector((state) => state.app);

  const onSubmit = (formData) => {
    const { id } = selected;
    const { trialEnd } = formData;
    const dataToSave = {
      id,
      ...formData,
      trialEnd: moment(trialEnd).valueOf(),
    };
    if (selectedContext === 'edit')
      dispatch(enterprisesActions.save(_.omitBy(dataToSave, _.isNaN)));
    else if (selectedContext === 'new')
      dispatch(enterprisesActions.add(_.omitBy(dataToSave, _.isNaN)));
  };
  const handleCloseClick = () => {
    if (!isDirty) onClose(false);
    else setIsCloseConfirmationOpen(true);
  };

  const availableFields = _.uniq(
    customersTypes
      .map((id) => {
        const props = metadata[id].properties;
        const keys = Object.keys(props);
        return keys.map((key) => props[key].label);
      })
      .flat()
      .sort()
  );

  return (
    <EditForm>
      <EditFormHeader
        title="Detalhes"
        onCloseClick={handleCloseClick}
        actions={
          permissions['dbg'] && (
            <IconButton
              onClick={() =>
                dispatch(appActions.setIsSecretActive(!isSecretActive))
              }
              className={clsx(isSecretActive && clsSecretActive)}
              size="large"
            >
              <BuildIcon />
            </IconButton>
          )
        }
      />
      <EditFormContent overflow="auto">
        {isSecretActive && (
          <FirebaseConsoleLink doc={`/enterprises/${selected.id}`} />
        )}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Box p={1}>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Dados Gerais
                </Typography>
              </Grid>
              <Grid container item spacing={2}>
                <Grid item md={12} sm={12} xs={12}>
                  <EditFormField
                    field={{
                      name: 'name',
                      label: 'Nome',
                      type: 'text',
                    }}
                    defaultValue={selected.name ?? ''}
                    control={control}
                    rules={{
                      required: 'Obrigatório',
                      minLength: { value: 3, message: 'Digite um nome válido' },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <EditFormField
                    field={{
                      name: 'ownerUid',
                      label: 'Proprietário',
                      type: 'select',
                      options: [...users]
                        .sort(({ displayName: a }, { displayName: b }) =>
                          a.localeCompare(b)
                        )
                        .map(({ id, displayName, email, createdAt }) => ({
                          value: id,
                          label: `${displayName} - ${email} - ${moment(
                            createdAt
                          ).format('DD/MM/YY HH:mm')}`,
                        })),
                    }}
                    defaultValue={selected.ownerUid ?? ''}
                    control={control}
                    rules={{
                      required: 'Obrigatório',
                    }}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <EditFormField
                    field={{
                      name: 'customersTypes',
                      label: 'Tipos de clientes',
                      type: 'select',
                      options: Object.keys(metadata)
                        .filter((id) => !!metadata[id].name)
                        .map((id) => ({
                          value: id,
                          label: metadata[id].name,
                        })),
                    }}
                    defaultValue={selected.customersTypes ?? []}
                    control={control}
                    multiple
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <EditFormField
                    field={{
                      name: 'defaultSendType',
                      label: 'Tipo padrão de envio',
                      type: 'select',
                      options: [
                        {
                          value: 'db',
                          label: 'Cadastro',
                        },
                        {
                          value: 'csv',
                          label: 'Arquivo',
                        },
                        {
                          value: 'custom-integration',
                          label: 'Integração',
                        },
                      ],
                    }}
                    defaultValue={selected.defaultSendType}
                    control={control}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <Box display="flex" flexWrap="wrap">
                    {availableFields.map((field) => (
                      <Box key={field} padding={0.3}>
                        <Chip label={field} />
                      </Box>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <EditFormField
                    field={{
                      name: 'inTrial',
                      label: 'Em avaliação',
                      type: 'boolean',
                    }}
                    defaultValue={selected.inTrial}
                    control={control}
                    rules={{}}
                  />
                </Grid>
                <Grid item xs={6}>
                  <EditFormField
                    field={{
                      name: 'trialEnd',
                      label: 'Término da avaliação',
                      type: 'date',
                    }}
                    defaultValue={
                      selected.trialEnd
                        ? moment(selected.trialEnd).format('YYYY-MM-DD')
                        : ''
                    }
                    control={control}
                    rules={{ required: 'Obrigatório' }}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <EditFormField
                    field={{
                      name: 'isActive',
                      label: 'Ativa',
                      type: 'boolean',
                    }}
                    defaultValue={selected.isActive ?? false}
                    control={control}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </form>
      </EditFormContent>
      <Divider />
      <EditFormFooter>
        <EditFormMessage>
          {Object.keys(errors).length > 0 && (
            <Typography color="error" variant="caption">
              Os erros devem ser corrigidos antes de salvar
            </Typography>
          )}
        </EditFormMessage>
        <EditFormAction>
          <ButtonWithProgress
            // type="submit"
            tooltipTitle="Salvar"
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
            disabled={!isDirty || isLoading}
            aria-label="salvar"
            startIcon={<SaveIcon />}
          >
            Salvar
          </ButtonWithProgress>
        </EditFormAction>
      </EditFormFooter>
      <ConfirmationDialog
        open={isCloseConfirmationOpen}
        title="Existem dados não salvos"
        actions={[
          {
            name: 'Cancelar',
            buttonProps: {
              autoFocus: true,
              onClick: () => {
                setIsCloseConfirmationOpen(false);
              },
            },
          },
          {
            name: 'Sair sem Salvar',
            buttonProps: {
              onClick: () => onClose(false),
            },
          },
          {
            name: 'Salvar',
            showLoading: true,
            buttonProps: {
              variant: 'contained',
              color: 'primary',
              startIcon: <SaveIcon />,
              onClick: handleSubmit(onSubmit),
            },
          },
        ]}
      >
        Alguns dados foram modificados e ainda não foram salvos.
        <br />
        <br />
        Deseja salvá-los antes de fechar a janela?
      </ConfirmationDialog>
    </EditForm>
  );
}

EditEnterprise.propTypes = {
  onClose: PropTypes.func.isRequired,
};
export default EditEnterprise;
